import React, { useState, useCallback, useEffect } from 'react';
import {
  Text,
  Card,
  Badge,
  Filters,
  Pagination,
  DataTable,
  FormLayout,
  Icon,
} from "@shopify/polaris";
import {
  AppsFilledIcon
} from '@shopify/polaris-icons';
import {
  developerAllApps,
  developerAllAppsSearch,
} from "../../dev_services/developer/api";
import "../../dev_pages/dev_app_page/dev_app_page.scss"
import NoDataFound from "../../../developers_module/dev_components/nodatafound/no_data_found";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from '@mui/material';

export default function DevAppPage() {
  const [availability, setAvailability] = useState<string[]>([]);
  const [productType, setProductType] = useState<string[]>([]);
  const [taggedWith, setTaggedWith] = useState<string>("");
  const [queryValue, setQueryValue] = useState("");
  const [apps, setApps] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);

  const [showPagination, setShowPagination] = useState(true); // Track pagination visibility

  const handleAvailabilityRemove = useCallback(() => setAvailability([]), []);
  const handleProductTypeRemove = useCallback(() => setProductType([]), []);
  const handleTaggedWithRemove = useCallback(() => setTaggedWith(""), []);
  // Fetch apps data from the API
  const fetchAppsData = useCallback(async (page: number) => {
    try {
      const response = await developerAllApps(page);
      if (response && "meta_data" in response) {
        const metaData = response.meta_data as {
          total_items: number;
          page: number;
          per_page: number;
          total_pages: number;
        };
        const totalPages = metaData.total_pages;
        console.log("Total Pages:", totalPages);
        setTotalPages(totalPages);
      }
      if (response && response.data.length > 0) {
        setApps(response.data);
      } else {
        setApps([]);
        setTotalPages(0); // Set total pages to 0 if no data found
      }
    } catch (error: any) {
      console.error("Error fetching apps data:", error.message);
      console.error("Error fetching apps data:", error);
      if (error.error) {
        navigate('/developer/login');
      }
      if (error.message) {
       
        setIsLoading(false);
      }
    }
  }, []);
  const handleQueryValueRemove = useCallback(() => {
    setQueryValue("");
    setShowPagination(true); // Ensure pagination is shown
    setCurrentPage(0); // Reset current page to 0
    fetchAppsData(0); // Fetch apps data for the first page
  }, [fetchAppsData]);
  // Inside handleFiltersQueryChange function
  const handleFiltersQueryChange = useCallback(async (value: string) => {
    setQueryValue(value);
    setCurrentPage(0); // Reset current page when a new search query is made
    try {
      const response = await developerAllAppsSearch(0, value);
      if (response.length === 0) {
        // No apps found for the given search query
        setApps([]);
        setTotalPages(0);
        setShowPagination(true);
      } else {
        setApps(response);
        setShowPagination(false);
      }
    } catch (error) {
      console.error("Error fetching apps data:", error);
    }
  }, []);
  const removeAddress = useCallback(async (value: string) => {
    setQueryValue(value);
    setCurrentPage(0); // Reset current page when a new search query is made
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    handleAvailabilityRemove();
    handleProductTypeRemove();
    handleTaggedWithRemove();
    handleQueryValueRemove();
  }, [
    handleAvailabilityRemove,
    handleQueryValueRemove,
    handleProductTypeRemove,
    handleTaggedWithRemove,
  ]);

  const filters = [];
  const appliedFilters: { key: string; label: string; onRemove: () => void }[] = [];
  if (!isEmpty(availability)) {
    const key = "availability";
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, availability),
      onRemove: handleAvailabilityRemove,
    });
  }
  if (!isEmpty(productType)) {
    const key = "productType";
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, productType),
      onRemove: handleProductTypeRemove,
    });
  }
  if (!isEmpty(taggedWith)) {
    const key = "taggedWith";
    appliedFilters.push({
      key,
      label: `Tagged with ${taggedWith}`,
      onRemove: handleTaggedWithRemove,
    });
  }
  const navigate = useNavigate();

  // Fetch data when the page changes
  useEffect(() => {
    fetchAppsData(currentPage);
  }, [currentPage, fetchAppsData]);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  useEffect(() => {
    if (apps.length === 0) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [apps]);

  return (
    <div className="developer-page-frame data-tabel">
      <div>
        {isLoading ? (
          <div style={{ height: "auto", paddingLeft: "2.0rem", paddingRight: "2.0rem" }}>
            <div
              style={{ display: "flex", paddingTop: "20px", paddingBottom: "20px" }}
              className="box-container"
            >
              <div style={{ alignItems: "center", height: "41px", gap: "10px", display: 'flex' }}>
                <Icon
                  source={AppsFilledIcon}
                  tone="base"
                />
                <label style={{ fontFamily: "sfprobold", fontSize: "20px", lineHeight: "revert" }} className="font-medium text-base">
                  Apps
                </label>
              </div>
              <button
                style={{
                  fontSize: "14px",
                  fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif",
                  fontWeight: "500",
                  marginLeft: "auto"  // This pushes the button to the end of the flex container
                }}
                className="button"
                onClick={() => navigate("/developer/createApps")}
              >
                Create app
              </button>
            </div>
            <div style={{ gap: "10px", display: "flex", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="rms-outline" style={{ width: "100%", }}>
                <Filters
                  queryValue={queryValue}
                  queryPlaceholder="Search apps"
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={removeAddress}
                  onQueryClear={handleQueryValueRemove}
                  onClearAll={handleFiltersClearAll}
                />
              </div>
              <button
                style={{
                  fontSize: "14px",
                  fontFamily: "sfpromedium",
                  fontWeight: "500",
                  marginLeft: "auto",
                  borderColor: "#c1c1c1",
                  background: "white",
                  borderWidth: "1px",
                  width: "100px",
                  borderRadius: "6px"
                }}
                onClick={() => handleFiltersQueryChange(queryValue)} // Call the function with queryValue
              >
                Search
              </button>
            </div>
            <div style={{ textAlign: "center", }}>
              <CircularProgress color="inherit" size={24} />
              <div> Please wait...</div>
            </div>
          </div>
        ) : (apps.length === 0 )? (
          <div style={{ height: "auto", paddingLeft: "2.2rem", paddingRight: "2.2rem" }}>
              <div style={{ height: "auto", paddingLeft: "2.2rem", paddingRight: "2.2rem" }}>
            <div
              style={{ display: "flex", paddingTop: "20px", paddingBottom: "20px" }}
              className="box-container">
              <div style={{ alignItems: "center", height: "41px", gap: "10px", display: 'flex' }}>
                <Icon
                  source={AppsFilledIcon}
                  tone="base"
                />
                <label style={{ fontFamily: "sfprobold", fontSize: "20px", lineHeight: "revert" }} className="font-medium text-base">
                  Apps
                </label>
              </div>

              <button
                style={{
                  fontSize: "14px",
                  fontFamily: "sfpromedium",
                  fontWeight: "500",
                  marginLeft: "auto"  // This pushes the button to the end of the flex container
                }}
                className="btn-apps"
                onClick={() => navigate("/developer/createApps")}
              >
                Create app
              </button>
            </div>
          
          
            <div style={{ paddingTop: "45px" }}></div>
          </div>
            <NoDataFound message="No apps found" />
          </div>
        ) : (
          <div style={{ height: "auto", paddingLeft: "2.2rem", paddingRight: "2.2rem" }}>
            <div
              style={{ display: "flex", paddingTop: "20px", paddingBottom: "20px" }}
              className="box-container">
              <div style={{ alignItems: "center", height: "41px", gap: "10px", display: 'flex' }}>
                <Icon
                  source={AppsFilledIcon}
                  tone="base"
                />
                <label style={{ fontFamily: "sfprobold", fontSize: "20px", lineHeight: "revert" }} className="font-medium text-base">
                  Apps
                </label>
              </div>

              <button
                style={{
                  fontSize: "14px",
                  fontFamily: "sfpromedium",
                  fontWeight: "500",
                  marginLeft: "auto"  // This pushes the button to the end of the flex container
                }}
                className="btn-apps"
                onClick={() => navigate("/developer/createApps")}
              >
                Create app
              </button>
            </div>
            <div style={{ gap: "10px", display: "flex", paddingTop: "20px", paddingBottom: "20px" }}>
              <div className="rms-outline" style={{ width: "100%", }}>
                <Filters
                  queryValue={queryValue}
                  queryPlaceholder="Search apps"
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={removeAddress}
                  onQueryClear={handleQueryValueRemove}
                  onClearAll={handleFiltersClearAll}
                />
              </div>
              <button
                style={{
                  fontSize: "14px",
                  fontFamily: "sfpromedium",
                  fontWeight: "500",
                  marginLeft: "auto",
                  borderColor: "#c1c1c1",
                  background: "white",
                  borderWidth: "1px",
                  width: "100px",
                  borderRadius: "6px"
                }}
                onClick={() => handleFiltersQueryChange(queryValue)} // Call the function with queryValue
              >
                Search
              </button>
            </div>
            <Card>
              <div className="row-h">
                {apps && apps.length > 0 && (
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                    ]}
                    headings={[
                      <span key="app-name" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        App Name
                      </span>,
                      <span key="total-install" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        Total Install
                      </span>,
                      <span key="listing-status" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        Listing Status
                      </span>,
                      <span key="overall-rating" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        Overall Rating
                      </span>,
                      <span key="created-at" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        Created At
                      </span>,
                      <span key="last-modified" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        Last Modified
                      </span>,
                    ]}
                    rows={apps.map((app) => [
                      <div key={app.id} style={{ color: "#2b6ecb", fontFamily: "-apple-system,BlinkMacSystemFont,San Francisco,Segoe UI,Roboto,Helvetica Neue,sans-serif", }}>
                        <a href={`/developer/${app.id}/appoverview`}>
                          {app.app_name}
                        </a>
                      </div>,
                      app.total_install,
                      app.status === "Draft" ? (
                        <Badge>{app.status}</Badge>
                      ) : app.status === "Reviewed" ? (
                        <Badge tone="success">{"Published"}</Badge>
                      ) : (
                        <Badge tone="info">{app.status}</Badge>
                      ),
                      app.overall_rating,
                      formatDate(app.created_at),
                      formatDate(app.updated_at),
                    ])}
                  />
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "10px",
                  padding: "10px"
                }}
              >
                {apps.length > 0 && (
                  <Pagination
                    hasNext={currentPage < totalPages - 1}
                    hasPrevious={currentPage > 0}
                    onPrevious={() => handlePageChange(currentPage - 1)}
                    onNext={() => handlePageChange(currentPage + 1)}
                  />
                )}
              </div>
            </Card>
            <div style={{ paddingTop: "45px" }}></div>
          </div>
        )}
      </div>
    </div>
  );

  function disambiguateLabel(key: string, value: string[]): string {
    switch (key) {
      case "taggedWith":
        return `Tagged with ${value}`;
      case "availability":
        return value.map((val) => `Available on ${val}`).join(", ");
      case "productType":
        return value.join(", ");
      default:
        return value.toString();
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  }

  function isEmpty(value: string | string[]): boolean {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === "" || value == null;
    }
  }
}
